import React from 'react';
import { MessageSquareText, ScanText, ListChecks, MousePointerClick } from 'lucide-react';

// The four steps follow the law-firm ad beat for beat: type the instruction
// (with Rename on), Analyze contents, review the preview, Apply. Claim limits
// (see the claim gate for the ad): OCR reads the first pages of a scan and
// only runs with Analyze contents on; unread scans go to _Needs Review; the
// only thing that moves files is Apply.
const STEPS = [
  {
    icon: MessageSquareText,
    title: 'Tell Sortio how your firm files',
    body:
      'Type it in plain English: find the client in each document, file it under the client and the matter, and rename it as date, document type, client, case. Turn on Rename.',
  },
  {
    icon: ScanText,
    title: 'It reads each document, even scans',
    body:
      "Click Analyze contents. Sortio reads each document to find the client, the matter, the date and the document type. Scanned PDFs get on-device OCR on Mac and Windows. Scans it can't read are set aside in a _Needs Review folder instead of being guessed.",
  },
  {
    icon: ListChecks,
    title: 'Check where each file goes, and why',
    body:
      "The preview shows each file's new name, its folder, and the reason Sortio chose it. Fix any it got wrong.",
  },
  {
    icon: MousePointerClick,
    title: 'Click Apply',
    body:
      'Nothing moves until you click Apply. Each sort is saved in Activity, where you can undo it.',
  },
];

const ExampleRow = ({ label, children, mono = true }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[88px_1fr] gap-1 sm:gap-4 py-3 border-t border-apple-700/50 first:border-t-0">
    <div className="text-xs font-medium uppercase tracking-wider text-apple-400 sm:pt-0.5">{label}</div>
    <div className={`text-sm text-apple-100 break-words ${mono ? 'font-mono' : ''}`}>{children}</div>
  </div>
);

const Workflow = () => {
  return (
    <section id="workflow" className="mt-4 mb-16 sm:my-20 scroll-mt-24">
      <div className="text-center mb-12 sm:mb-14">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          How it works
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          From a folder of badly named scans to documents filed by client and matter, in four
          steps. You check the plan before anything moves.
        </p>
      </div>

      <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 list-none">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <li
              key={i}
              className="relative bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-6 flex flex-col"
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mr-3">
                  <Icon className="text-sortio-primary" size={18} aria-hidden="true" />
                </div>
                <span className="text-xs font-medium uppercase tracking-wider text-apple-400">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="text-lg font-medium text-apple-50 mb-2">{step.title}</h3>
              <p className="text-sm text-apple-200 leading-relaxed">{step.body}</p>
            </li>
          );
        })}
      </ol>

      {/* One worked example, shaped like a row of the app's sort preview. */}
      <div className="mt-10 max-w-3xl mx-auto bg-apple-800/40 backdrop-blur-sm rounded-2xl border border-apple-700/50 p-5 sm:p-8">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">
            One file in the preview
          </span>
          <span className="text-xs text-apple-400">Example</span>
        </div>

        <div className="rounded-xl bg-black/30 border border-apple-700/40 px-4 sm:px-5 py-2">
          <ExampleRow label="Before">scan_0047.pdf</ExampleRow>
          <ExampleRow label="New name">2026-06-18 Deposition - Jane Doe - Doe v. Acme Corp.pdf</ExampleRow>
          <ExampleRow label="Goes to">Clients / Doe, Jane / 2026-014 Doe v. Acme Corp / Depositions</ExampleRow>
          <ExampleRow label="Why" mono={false}>
            Deposition transcript dated June 18, 2026, in Doe v. Acme Corp., which matches the
            client folder for Jane Doe and matter 2026-014.
          </ExampleRow>
        </div>

        <p className="text-xs text-apple-400 mt-4 leading-relaxed">
          Illustration with fictional names. You set the naming pattern and the folder structure,
          review each new name, and can change where any file goes before you click Apply.
        </p>
      </div>
    </section>
  );
};

export default Workflow;
